import { createAsyncThunk } from '@reduxjs/toolkit';
import api from 'src/app/api';
import { buildEncodedQueryString, CSVDataType, DateTimeFormat, downloadCsv, getLocalDateTime } from 'src/common';
import { InitiateReportRequest, InitiateReportRequestModel } from 'src/features/clients/client/reportsV2/store/types';
import { PagedResult } from 'src/store';
import { REPORT_STATUS_NAMES } from '../constants';
import {
  AfslViewModel,
  BulkReportJob,
  BulkReportList,
  BulkReportRequest,
  ClientsByIdsViewModel,
  ClientSearchParameters,
  ClientSearchResult,
  CreateBulkReportRequest,
  DownloadBulkReportsCsvRequest,
  FilteredReportClientViewModel,
  GetFilteredReportClientsQuery,
  GetReportParametersRequest,
  GetReportParametersViewModel,
  ImmediateBulkReportByIdViewModel,
  ListScheduledEventRequest,
  ModelItem,
  ScheduledEvent,
  SetReportingParameterSystemShareRequest,
  ValidateClientIdRequest,
} from './types';

export enum ReportingActionTypes {
  FetchAfsls = '@@reporting/FetchAfsls',
  FetchSecurities = '@@reporting/FetchSecurities',
  FetchModelList = '@@reporting/FetchModelList',
  FetchFilteredClients = '@@reporting/FetchFilteredClients',
  FetchBulkReportList = '@@reporting/FetchBulkReportList',
  FetchScheduledEventList = '@@reporting/FetchScheduledEventList',
  CreateBulkReport = '@@reporting/CreateBulkReport',
  UpdateBulkReport = '@@reporting/UpdateBulkReport',
  DownloadBulkReports = '@@reporting/DownloadBulkReports',
  DownloadBulkJobsCsv = '@@reporting/DownloadBulkJobsCsv',
  DeleteScheduledEvent = '@@reporting/DeleteScheduledEvent',
  InitiateReportGeneration = '@@client/reportsV2/InitiateReportGeneration',
  GetReportById = '@@client/reportsV2/GetReportById',
  GetImmediateBulkReportById = '@@/reporting/batch/GetImmediateBulkReportById',
  DeleteBulkReport = '@@reporting/DeleteBulkReport',
  ReportParamListTemplates = '@@reporting/ReportParamListTemplates',
  ReportParamSetSystemShare = '@@reporting/ReportParamSetSystemShare',
  ReportParamDeleteTemplate = '@@reporting/ReportParamDeleteTemplate',
  GetFilteredReportClients = '@@reporting/GetFilteredReportClients',
  ValidateClientIds = '@@reporting/ValidateClientIds',
}

export enum ReportingApi {
  FetchAfsls = '/bff/GetAfslsPracticesAdvisersForUser',
  FetchSecurities = '/securities/GetAllSimpleSecurityList',
  FetchModelList = '/strategies/GetModelList',
  FetchFilteredClients = '/bff/GetFilteredPortfolios',
  FetchBulkReportList = '/reporting/batch/ListBulkReport',
  FetchScheduledEventList = '/reporting/scheduledevent/ListScheduledEvent',
  CreateBulkReport = '/reporting/batch/CreateBulkReport',
  UpdateBulkReport = '/reporting/batch/UpdateBulkReport',
  DeleteScheduledEvent = '/reporting/scheduledevent/DeleteScheduledEvent',
  InitiateReportGeneration = '/reporting/InitiateReportRequest',
  GetReportById = '/reporting/GetReportById',
  GetImmediateBulkReportById = '/reporting/batch/GetImmediateBulkReportById',
  DeleteBulkReport = '/reporting/batch/DeleteBulkReport',
  ReportParamListTemplates = '/reporting/ReportParamListTemplates',
  ReportParamSetSystemShare = '/reporting/ReportParamSetSystemShare',
  ReportParamDeleteTemplate = '/reporting/ReportParamDeleteTemplate',
  GetFilteredReportClients = '/bff/GetFilteredReportClients',
  ValidateClientIds = '/bff/GetValidatedClientList',
}

export const downloadBulkJobsCsv = createAsyncThunk(ReportingActionTypes.DownloadBulkJobsCsv, async (request: DownloadBulkReportsCsvRequest) => {
  try {
    const response = await api.post<BulkReportList>(ReportingApi.FetchBulkReportList, {
      pageSize: 999999,
      page: 1,
      draft: false,
      scheduledOnly: false,
      queryFields: request.queryFields,
    });

    const data =
      request.selectedRowIds.length === 0
        ? response.data.results
        : response.data.results.filter((record) => request.selectedRowIds.includes(record.id));
    const csvData: CSVDataType[] = data.map((record) => ({
      ID: record.id,
      Name: record.name,
      'Created By': record.createdById,
      'Schedule Status': record.scheduleInstanceDetails?.reviewStatus
        ? REPORT_STATUS_NAMES[record.scheduleInstanceDetails?.reviewStatus]
        : 'Unavailable',
      Progress: `Completed ${record.progress.completedRecords} out of ${record.progress.totalRecords}`,
      'Updated On': record.updatedOn ? getLocalDateTime(record.updatedOn.toString(), DateTimeFormat.DateTimeShort) : '',
      'Created On': record.createdOn ? getLocalDateTime(record.createdOn.toString(), DateTimeFormat.DateTimeShort) : '',
    }));
    downloadCsv(csvData, `ReportJobs`);
    return { message: 'CSV downloaded' };
  } catch (error) {
    return Promise.reject('Error downloading CSV');
  }
});

export const deleteScheduledEvent = createAsyncThunk(ReportingActionTypes.DeleteScheduledEvent, async (id: string) => {
  const queryString = buildEncodedQueryString({
    id: id,
  });
  await api.delete(`${ReportingApi.DeleteScheduledEvent}${queryString}`);
});

export const updateBulkReport = createAsyncThunk<BulkReportJob, CreateBulkReportRequest>(
  ReportingActionTypes.UpdateBulkReport,
  async (payload: CreateBulkReportRequest) => {
    const response = await api.put(`${ReportingApi.UpdateBulkReport}`, payload);
    return Promise.resolve<BulkReportJob>(response.data);
  }
);

export const createBulkReport = createAsyncThunk<BulkReportJob, CreateBulkReportRequest>(
  ReportingActionTypes.CreateBulkReport,
  async (payload: CreateBulkReportRequest) => {
    const response = await api.post(`${ReportingApi.CreateBulkReport}`, payload);
    return Promise.resolve<BulkReportJob>(response.data);
  }
);

export const deleteBulkReport = createAsyncThunk(ReportingActionTypes.DeleteBulkReport, async (id: string) => {
  const queryString = buildEncodedQueryString({
    batchId: id,
  });
  await api.delete(`${ReportingApi.DeleteBulkReport}${queryString}`);
});

export const fetchBulkReportList = createAsyncThunk(ReportingActionTypes.FetchBulkReportList, async (request: BulkReportRequest) => {
  const response = await api.post<BulkReportList>(ReportingApi.FetchBulkReportList, request);
  return response.data;
});

export const fetchAfsls = createAsyncThunk(ReportingActionTypes.FetchAfsls, async () => {
  const response = await api.get<AfslViewModel>(`${ReportingApi.FetchAfsls}`);
  return response.data;
});

export const fetchSecurities = createAsyncThunk(ReportingActionTypes.FetchSecurities, async () => {
  const response = await api.get(`${ReportingApi.FetchSecurities}`);
  return response.data;
});

export const fetchModelList = createAsyncThunk(ReportingActionTypes.FetchModelList, async (afslIds?: number[]) => {
  const response = await api.post(`${ReportingApi.FetchModelList}`, afslIds || []);
  return Promise.resolve<ModelItem[]>(response.data);
});

export const fetchFilteredClients = createAsyncThunk(ReportingActionTypes.FetchFilteredClients, async (parameters: ClientSearchParameters) => {
  const queryString = buildEncodedQueryString({
    afslIds: parameters.afsls.map((i) => i.afslId),
    practiceIds: parameters.practices.map((i) => i.practiceId),
    adviserIds: parameters.advisers.map((i) => i.advisorId),
    clientTypes: parameters.clientTypes,
    statuses: parameters.statuses,
    securityHeld: parameters.securityHeld,
    serviceType: parameters.serviceType,
    portfolioValueFrom: parameters.portfolioValueFrom,
    portfolioValueTo: parameters.portfolioValueTo,
    availableCashBalanceFrom: parameters.availableCashBalanceFrom,
    availableCashBalanceTo: parameters.availableCashBalanceTo,
    modelId: parameters.modelId,
    securityId: parameters.securityId,
    isDataFromTriumph: parameters?.isDataFromTriumph ?? false,
  });
  const response = await api.get<ClientSearchResult[]>(`${ReportingApi.FetchFilteredClients}${queryString}`);
  return response.data;
});

export const fetchScheduledEventList = createAsyncThunk(ReportingActionTypes.FetchScheduledEventList, async (request: ListScheduledEventRequest) => {
  const queryString = buildEncodedQueryString({
    page: request.page,
    pageSize: request.pageSize,
  });
  const response = await api.get<PagedResult<ScheduledEvent>>(`${ReportingApi.FetchScheduledEventList}${queryString}`);
  return response.data;
});

export const initiateReportGeneration = createAsyncThunk(ReportingActionTypes.InitiateReportGeneration, async (request: InitiateReportRequest) => {
  const response = await api.post<InitiateReportRequestModel>(`${ReportingApi.InitiateReportGeneration}`, request);
  return response.data;
});

export const getImmediateBulkReportById = createAsyncThunk(ReportingActionTypes.GetImmediateBulkReportById, async (requestId: string) => {
  const queryString = buildEncodedQueryString({
    batchId: requestId,
  });
  const response = await api.get<ImmediateBulkReportByIdViewModel>(`${ReportingApi.GetImmediateBulkReportById}${queryString}`);
  return response.data;
});

export const fetchReportingParameters = createAsyncThunk(
  ReportingActionTypes.ReportParamListTemplates,
  async (request: GetReportParametersRequest) => {
    const queryString = buildEncodedQueryString({
      includeSharedAfsl: request.includeSharedAfsl,
      kind: request.kind,
    });
    const response = await api.post<PagedResult<GetReportParametersViewModel>>(`${ReportingApi.ReportParamListTemplates}${queryString}`, {
      pageNumber: request.page + 1,
      pageSize: request.pageSize,
      queryFields: request.queryFields,
    });
    return response.data;
  }
);

export const setReportingParameterSystemShare = createAsyncThunk(
  ReportingActionTypes.ReportParamSetSystemShare,
  async (request: SetReportingParameterSystemShareRequest) => {
    const queryString = buildEncodedQueryString({
      reportParameterId: request.reportParameterId,
      systemShare: request.systemShared,
    });
    const response = await api.post<InitiateReportRequestModel>(`${ReportingApi.ReportParamSetSystemShare}${queryString}`);
    if (response.status !== 200) {
      return {
        message: 'Failed to update system sharing for reporting parameters.',
        variant: 'error',
      };
    }

    return {
      message: !request.systemShared ? 'System sharing disabled.' : 'System sharing enabled.',
      variant: 'success',
    };
  }
);

export const deleteReportingParameter = createAsyncThunk(ReportingActionTypes.ReportParamDeleteTemplate, async (reportParameterId: string) => {
  const queryString = buildEncodedQueryString({
    reportParameterId: reportParameterId,
  });

  const response = await api.post(`${ReportingApi.ReportParamDeleteTemplate}${queryString}`);
  if (response.status !== 200) {
    return { message: 'Unable to delete saved reporting parameters.', variant: 'error' };
  }
  return { message: 'Saved reporting parameters deleted.', variant: 'success' };
});

export const fetchFilteredReportClients = createAsyncThunk(
  ReportingActionTypes.GetFilteredReportClients,
  async (parameters: GetFilteredReportClientsQuery) => {
    const queryString = buildEncodedQueryString({
      ...parameters,
    });
    const response = await api.get<FilteredReportClientViewModel>(`${ReportingApi.GetFilteredReportClients}${queryString}`);
    return response.data;
  }
);

export const validateClientIds = createAsyncThunk(ReportingActionTypes.ValidateClientIds, async (request: ValidateClientIdRequest) => {
  const response = await api.post<PagedResult<ClientsByIdsViewModel>>(ReportingApi.ValidateClientIds, {
    pagedRequest: {
      pageNumber: 1,
      pageSize: request.clientIds.length,
    },
    clientIds: request.clientIds,
  });
  return Promise.resolve<PagedResult<ClientsByIdsViewModel>>(response.data);
});
